import { MarketingChannel } from "@redotech/redo-billing-common/marketing-channel";
import { RedoHorizontalTabs } from "@redotech/redo-web/arbiter-components/tabs/redo-horizontal-tabs";
import { Flex } from "@redotech/redo-web/flex";
import { TabsPortal } from "@redotech/redo-web/tabs-portal";
import { memo, useEffect, useState } from "react";

import { MarketingAttributionEvent } from "@redotech/redo-marketing-templates-common/marketing";
import { createTabConfig, useTabs } from "@redotech/redo-web/utils/use-tabs";
import { MarketingAttributionEventsFilter } from "./filters/marketing-attribution-events-filter";
import { MarketingAttributionFilter } from "./filters/marketing-attribution-filter";
import {
  ChannelFilter,
  MarketingChannelFilter,
} from "./filters/marketing-channel-filter";
import { MarketingDateRangeFilter } from "./filters/marketing-date-range-filter";
import { MarketingAnalyticsAudience } from "./marketing-analytics-sections/marketing-analytics-audience";
import { MarketingAnalyticsBackInStock } from "./marketing-analytics-sections/marketing-analytics-back-in-stock";
import { MarketingAnalyticsOverview } from "./marketing-analytics-sections/marketing-analytics-overview";

export enum MarketingAnalyticsPage {
  OVERVIEW = "overview",
  AUDIENCE = "audience",
  BACK_IN_STOCK = "back-in-stock",
}

export const MARKETING_ANALYTICS_TABS_URL_PARAM = "tab";

const tabConfig = createTabConfig(MarketingAnalyticsPage, {
  [MarketingAnalyticsPage.OVERVIEW]: { component: MarketingAnalyticsOverview },
  [MarketingAnalyticsPage.AUDIENCE]: { component: MarketingAnalyticsAudience },
  [MarketingAnalyticsPage.BACK_IN_STOCK]: {
    component: MarketingAnalyticsBackInStock,
  },
});

export const MarketingAnalytics = memo(function MarketingAnalytics() {
  const [attributionDays, setAttributionDays] = useState(5);
  const [channelFilter, setChannelFilter] = useState<ChannelFilter>("all");
  const [attributionEvents, setAttributionEvents] = useState<
    MarketingAttributionEvent[]
  >(["click", "open", "delivery"]);

  const [dateFilter, setDateFilter] = useState<
    [Temporal.PlainDate, Temporal.PlainDate] | undefined
  >(() => {
    const today = Temporal.Now.plainDateISO();
    const thirtyDaysAgo = today.subtract({ days: 30 });
    return [thirtyDaysAgo, today];
  });

  // Reset attribution events when channel filter changes
  useEffect(() => {
    if (channelFilter === MarketingChannel.EMAIL) {
      // Email channel: exclude delivery, keep valid events
      setAttributionEvents((prev) =>
        prev.filter((event) => event !== "delivery"),
      );
    } else if (channelFilter === MarketingChannel.SMS) {
      // SMS channel: exclude opens, keep valid events
      setAttributionEvents((prev) => prev.filter((event) => event !== "open"));
    } else {
      // All channels: reset to all events if currently empty or invalid
      setAttributionEvents((prev) => {
        if (prev.length === 0) {
          return ["click", "open", "delivery"];
        }
        return prev;
      });
    }
  }, [channelFilter]);

  const { selectedTab, setSelectedTab, tabs, SelectedTabComponent } = useTabs({
    tabConfig,
    urlParam: MARKETING_ANALYTICS_TABS_URL_PARAM,
  });

  return (
    <Flex
      dir="column"
      gap={
        selectedTab === MarketingAnalyticsPage.BACK_IN_STOCK ? "none" : "4xl"
      }
      w="full"
    >
      <TabsPortal>
        <RedoHorizontalTabs<MarketingAnalyticsPage>
          drawBottomLine={false}
          pl="none"
          selectedTab={selectedTab}
          setSelectedTab={setSelectedTab}
          size="xs"
          tabs={tabs}
        />
      </TabsPortal>
      {selectedTab !== MarketingAnalyticsPage.BACK_IN_STOCK && (
        <Flex dir="row" gap="md">
          {selectedTab === MarketingAnalyticsPage.OVERVIEW && (
            <>
              <MarketingAttributionFilter
                attributionFilter={attributionDays}
                setAttributionFilter={setAttributionDays}
              />
              <MarketingChannelFilter
                channelFilter={channelFilter}
                setChannelFilter={setChannelFilter}
              />
              <MarketingAttributionEventsFilter
                channelFilter={channelFilter}
                onChange={setAttributionEvents}
                selectedEvents={attributionEvents}
              />
            </>
          )}
          {selectedTab === MarketingAnalyticsPage.AUDIENCE && (
            <MarketingChannelFilter
              channelFilter={channelFilter}
              setChannelFilter={setChannelFilter}
            />
          )}
          <MarketingDateRangeFilter
            dateFilter={dateFilter}
            setDateFilter={setDateFilter}
          />
        </Flex>
      )}
      <SelectedTabComponent
        attributionDays={attributionDays}
        attributionEvents={attributionEvents}
        channelFilter={channelFilter}
        dateFilter={dateFilter}
      />
    </Flex>
  );
});
